import "react-sliding-pane/dist/react-sliding-pane.css";

import {
    Back,
    Container,
    FormField,
    Header,
    Title,
    Wrapper,
    WrapperEmployee,
} from "./styles/SearchEmployee.styles";
import React, { useCallback, useContext, useEffect, useState } from "react";

import Context from "features/allocations/contexts/Context";
import CustomGroupContext from "features/allocations/contexts/CustomGroupContext";
import EmployeeDrawer from "./EmployeeDrawer";
import GenericSidebarPortal from "shared/components/feedback/modals/common/GenericSidebarPortal";
import IconTextInput from "shared/components/inputs/icon-input/IconTextInput";
import Left from "shared/components/utils/icons/Left";
import Magnifier from "shared/components/utils/icons/Magnifier";
import Profile from "shared/components/data-display/card/Profile";
import PropTypes from "prop-types";
import Right from "shared/components/utils/icons/Right";
import Text from "shared/components/data-display/mui/Text";
import env from "react-dotenv";
import { useLocalStorage } from "shared/hooks/useLocalStorage";
import { useUser } from "shared/services/api/useUser";

const SearchEmployee = (props) => {
    const { defaultValue } = props;
    const { getEmployeesForSearch } = useUser();
    const { setWithExpiry } = useLocalStorage();
    const { getWithExpiry } = useLocalStorage();
    const { get } = useLocalStorage();
    const { set } = useLocalStorage();
    const [employees, setEmployees] = useState([]);
    const [showHistory, setShowHistory] = useState(true);
    const [cache, setCache] = useState(null);

    const customGroupConsumer = useContext(CustomGroupContext);
    const contextConsumer = useContext(Context);
    const [drawerUserId, setDrawerUserId] = useState();

    const filterByName = useCallback((data, inputValue) => {
        const results = data.filter((object) => {
            return object.SearchText.includes(inputValue.toLowerCase());
        });
        setEmployees(results);
    }, []);

    const handleOnChange = useCallback(async function (inputValue) {
        if (inputValue && cache) {
            filterByName(cache, inputValue);
            setShowHistory(false);
        } else {
            setShowHistory(true);
        }
    }, [cache, filterByName]);

    useEffect(() => {
        if (defaultValue && cache) {
            handleOnChange(defaultValue);
        }
    }, [cache, defaultValue, handleOnChange]);

    useEffect(() => {
        async function getEmployees() {
            let list = await getWithExpiry("employees-search");
            if (list == null) {
                try {
                    const payload = await getEmployeesForSearch([{ name: "$name", value: "" }]);
                    setWithExpiry(
                        "employees-search",
                        payload,
                        env.BESTWORK_REACT_EMPLOYEEFORSEARCH_EXPIRYMIN
                    );
                    list = payload;
                } catch {
                    // Error silently
                }
            }
            setCache(list);
        }

        getEmployees();
    }, [getEmployeesForSearch, getWithExpiry, setWithExpiry]);

    const debounce = (func, delay) => {
        let debounceTimer;
        return (...args) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => func(...args), delay);
        };
    };

    const optimisedHandleChange = debounce(handleOnChange, 500);

    useEffect(() => {
        const getHistory = () => {
            let data = get("employees-search-history");
            if (data) {
                setEmployees(data);
            }
            else {
                setEmployees([]);
            }
        };

        if (showHistory) {
            getHistory();
        }
    }, [showHistory, get]);

    const handleDrawerClose = () => {
        setDrawerUserId(null);
    };

    const handleSelect = (employee) => {
        saveHistory(employee);
        setDrawerUserId(employee.Id);
    };

    const saveHistory = (employee) => {
        // Get current history
        let currentData = get("employees-search-history") || [];
        //remove duplicates
        currentData = currentData.filter((data) => data.Id !== employee.Id);
        //save selected
        currentData.unshift(employee);
        set(
            "employees-search-history",
            currentData.slice(
                0,
                env.BESTWORK_REACT_EMPLOYEEFORSEARCH_HISTORYLIMIT
            )
        );
    };

    const searchResultLabel = employees.length
        ? "Search Results (" + employees.length + ")"
        : "No Results";
    const searchLabel = showHistory ? "Recently Viewed" : searchResultLabel;

    return (
        <div id="sidebarSearchEmployeeAllocations">
            <Container>
                <Header>
                    <Title>
                        <Text variant="xxlargeBold">Search Employees</Text>
                    </Title>
                    <FormField>
                        <IconTextInput
                            label="Employee Name"
                            defaultValue={defaultValue}
                            type="text"
                            icon={<Magnifier />}
                            onChange={optimisedHandleChange}
                        />
                    </FormField>

                    <Text variant="xsmallLight">{searchLabel}</Text>
                </Header>
                <Wrapper>
                    {employees?.map((employee) => (
                            <WrapperEmployee
                                key={employee.Id}
                                onClick={() => handleSelect(employee)}
                            >
                                <Profile
                                    name={employee.FullName}
                                    title={employee.Title}
                                    location={employee.Office}
                                    image={
                                        env.BESTWORK_REACT_ASSET_DOMAIN +
                                        "/" +
                                        employee.PhotoURL
                                    }
                                    freelance={false}
                                />
                                <Right circle={false} />
                            </WrapperEmployee>
                        ))}
                </Wrapper>
            </Container>

            {drawerUserId && (
                <Context.Provider value={contextConsumer}>
                    <CustomGroupContext.Provider value={customGroupConsumer}>
                        <GenericSidebarPortal
                            open={true}
                            title={
                                <Back onClick={handleDrawerClose}>
                                    <Left circle={false} />
                                    <Text variant="small">Results</Text>
                                </Back>
                            }
                            content={
                                <EmployeeDrawer
                                    userId={drawerUserId}
                                    onClose={props.onClose}
                                />
                            }
                            onClose={handleDrawerClose}
                        />
                    </CustomGroupContext.Provider>
                </Context.Provider>
            )}
        </div>
    );
};

SearchEmployee.propTypes = {
    defaultValue: PropTypes.string,
    onClose: PropTypes.func,
};

export default SearchEmployee;
